import { useEffect, useState } from "react";
import { process } from "@progress/kendo-data-query";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Label,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabPane,
  TabContent,
} from "reactstrap";
import classnames from "classnames";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { ColumnMenu } from "../components/kendoComponents/columnMenu/ColumnMenu";
import { ColumnProps } from "../components/kendoComponents/columnProps/ColumnProps";
import { GridPager } from "../components/kendoComponents/gridPager/GridPager";
import "../assets/scss/kendo-theme-custom.scss";
import "./processRuns.scss";
import moment from "moment";

import {
  Chart,
  ChartTooltip,
  ChartXAxis,
  ChartXAxisItem,
  ChartYAxis,
  ChartYAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartLegend,
} from "@progress/kendo-react-charts";
import "react-hammerjs";

import { SetCurrentActiveTab } from "../store/customizer/CustomizerSlice";

import LoaderSpin from "../components/loaderSpin/LoaderSpin";

import { useNavigate } from "react-router-dom";
import DataProductTreeSelector from "../components/DataQuality/DataProductTreeSelector";
import ProcessSelector from "../components/DataQuality/ProcessSelector";
import ExtendedStatsSelector from "../components/DataQuality/ExtendedStatsSelector";
import FilterIntervallo from "../components/DataQuality/FilterIntervallo";
import FilterDataDa from "../components/DataQuality/FilterDataDa";
import FilterDataA from "../components/DataQuality/FilterDataA";
import FilterSubmit from "../components/DataQuality/FilterSubmit";
import FilterOverlay from "../components/filterOverlay/FilterOverlay";
import { fetchProcessRunsTimeline } from "../store/app/ProcessRuns/ProcessRunsSlice";
import DateCell from "../components/kendoComponents/dateCell/DateCell";
import PropTypes from "prop-types";
import AtlanCell from "../components/customRender/renders/Renders";


const ProcessGridCell = ({ props, type, navigate, searchParams }) => {
  const { dataItem } = props;
  const { version_id, domain_name, process_name, reference_date, purpose_name, status_name, ext_status_name, process_type, process_desc, is_manual_validation, revision_num, release_date, release_notes, release_author } = dataItem;

  const handleClick = (e) => {
    e.preventDefault();
    if (type === "version_id") {
      navigate(`/versioni/detail/${version_id}?${searchParams}`);
    }
  };

  const getTitle = () => {
    if (type === "version_id") {
      return `Domain:${domain_name} Reference:${reference_date} Purpose:${purpose_name} Status:${status_name} Ext.Status:${ext_status_name}`;
    }
    return `Process:${process_type} Description:${process_desc} Validation:${is_manual_validation} Revision:${revision_num} Release:${release_date} Notes:${release_notes} Author:${release_author}`;
  };

  const displayValue =
    type === "version_id" ? version_id : process_name;
  if (type === "process_name") {
    return (
      <td>
        <span title={getTitle()}>
          {displayValue}
        </span>
      </td>
    );
  }

  return (
    <td>
      <button
        type="button"
        style={{
          cursor: "pointer",
          background: "none",
          border: "none",
          padding: 0,
          textDecoration: "underline"
        }}
        className="fw-bold text-primary"
        title={getTitle()}
        onClick={handleClick}
      >
        {displayValue}
      </button>
    </td>
  );
};


ProcessGridCell.propTypes = {
  type: PropTypes.oneOf(["version_id", "process_name"]).isRequired,
  props: PropTypes.object.isRequired,
  dataItem: PropTypes.object.isRequired,
  version_id: PropTypes.string,
  domain_name: PropTypes.string,
  reference_date: PropTypes.string,
  purpose_name: PropTypes.string,
  status_name: PropTypes.string,
  ext_status_name: PropTypes.string,
  process_type: PropTypes.string,
  process_desc: PropTypes.string,
  is_manual_validation: PropTypes.bool,
  revision_num: PropTypes.number,
  release_date: PropTypes.string,
  release_notes: PropTypes.string,
  release_author: PropTypes.string,
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.any.isRequired,
};

const RunIdCell = (props, searchParams) => {
  const { dataItem } = props;
  const { version_id, run_id } = dataItem;
  const navigate = useNavigate();

  return (
    <td>
      <button
        type="button"
        style={{
          cursor: "pointer",
          background: "none",
          border: "none",
          padding: 0,
          textDecoration: "underline"
        }}
        className="fw-bold text-primary"
        onClick={() => {
          navigate(
            "/process-runs/detail/application-log/" +
            version_id +
            "?" +
            searchParams
          );
        }}
        onKeyDown={() => console.log("onKeyDown")}
      >
        {run_id}
      </button>
    </td>
  );
};

RunIdCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
  version_id: PropTypes.string,
  run_id: PropTypes.string,  
};

const DataCatalogCell = (props, navigate, searchParams) => {

  const { dataItem } = props;
  const { product_type_id, product_name } = dataItem;

  const handleClick = () => {
    navigate(`/data-catalog/details/${product_type_id}?${searchParams.toString()}`);
  };

  return (
    <td>
      <button
        type="button"
        style={{
          cursor: "pointer",
          background: "none",
          border: "none",
          padding: 0,
          textDecoration: "underline"
        }}
        className="fw-bold text-decoration-underline"
        onClick={handleClick}
      >
        {product_name}
      </button>
    </td>
  );
};

DataCatalogCell.propTypes = {  
  dataItem: PropTypes.object.isRequired,
  product_type_id: PropTypes.string,
  product_name: PropTypes.string,
};

const ProcessRuns = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const isSpinnerVisible = useSelector(
    (state) => state.dataQualityReducer.loading
  );
  const dataCheck = useSelector((state) => state.dataQualityReducer.dataCheck);
  const refreshCount = useSelector(
    (state) => state.dataQualityReducer.refreshCount
  );
  const processRunsList = useSelector(
    (state) => state.processRunsReducer.processRunsList || []
  );

  const filterIntervallo = useSelector(
    (state) => state.dataQualityReducer.filterIntervallo
  );

  const filterDataDa = useSelector(
    (state) => state.dataQualityReducer.filterDataDa
  );
  const filterDataA = useSelector(
    (state) => state.dataQualityReducer.filterDataA
  );
  const filterDomains = useSelector(
    (state) => state.dataQualityReducer.filterDomains
  );
  const filterDataProducts = useSelector(
    (state) => state.dataQualityReducer.filterDataProducts
  );
  const filterProcessTypes = useSelector(
    (state) => state.processRunsReducer.filterProcessTypes
  );
  const filterExtendedStatsDomains = useSelector(
    (state) => state.dataQualityReducer.filterExtendedStatsDomains
  );

  const [dataState, setDataState] = useState({
    sort: [
      {
        field: "code",
        dir: "asc",
      },
    ],
    skip: 0,
    take: 20,
  });

  const [dataResult, setDataResult] = useState(
    process(processRunsList, dataState)
  );

  const groupBy = (array, key) => {
    // Return the end result
    return array.reduce((result, currentValue) => {
      // If an array already present for key, push it to the array. Else create an array and push the object
      if (!result[currentValue[key]]) {
        result[currentValue[key]] = { key: currentValue[key], data: [] };
      }

      result[currentValue[key]].data.push(currentValue);
      // Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
      return result;
    }, []);
  };

  const transform = (processRuns) => {
    let c = [];
    //Creazione Array Compatibile con KendoChart
    for (const status of Object.keys(processRuns)) {
      c.push(processRuns[status]);
    }
    return c;
  };

  const onSeriesClick = (event) => {
    navigate(
      "/process-runs/detail/application-log/" +
      event.dataItem.version_id +
      "?" +
      searchParams
    );
    // console.log ('SERIES CLICK ', event)
  };

  const mapSeries = (item, idx) => (
    <ChartSeriesItem
      key={idx}
      type="scatter"
      data={item.data}
      name={item.key}
      xField="run_start_ts"
      yField="durationMs"
      color={item?.data[idx]?.info?.color}
      markers={{
        visible: true,
        type: "circle",
        size: 12,
        background: item?.data[idx]?.info?.color,
      }}
    />
  );

  const renderTooltip = (context) => {
    return (
      <div style={{ padding: 16 + "px" }}>
        Process Name: {context?.point.dataItem.process_name}
        <br />
        Start time:{" "}
        {moment(context?.point.dataItem.run_start_ts).format(
          "DD/MM/Y - HH:mm:ss"
        )}{" "}
        <br />
        Duration: {context?.point.dataItem.duration}
        <br />
        Execution Status: {context?.point.dataItem.run_status}
        <br />
        Data Product: {context?.point.dataItem.product_name}
        <br />
        Version: {context?.point.dataItem.version_id}
        <br />
        Version Status: {context?.point.dataItem.ext_status_name}
      </div>
    );
  };

  const diffDays = () => {
    const dateFormat = "YYYY-MM-DD HH:mm:ss";
    return moment(searchParams.get("toDate"), dateFormat).diff(
      moment(searchParams.get("fromDate"), dateFormat),
      "days"
    );
  };

  useEffect(() => {
    dispatch(
      fetchProcessRunsTimeline(
        filterDataDa,
        filterDataA,
        filterProcessTypes?.toString(),
        filterDomains,
        filterExtendedStatsDomains,
        filterDataProducts
      )
    );

    return () => {
      dispatch(SetCurrentActiveTab("1"));
    };

  }, [navigate, refreshCount]);

  useEffect(() => {
    setDataState((prev) => ({
      ...{
        sort: [
          {
            field: "code",
            dir: "asc",
          },
        ],
      },
      skip: 0,
      take: 20,
    }));
    setDataResult(process(processRunsList, dataState));
  }, [processRunsList]);

  const currentActiveTab = useSelector(
    (state) => state.customizer.currentActiveTab
  );

  // Toggle active state for Tab
  const toggle = (tab) => {
    if (currentActiveTab !== tab) dispatch(SetCurrentActiveTab(tab));
  };

  const renderProcessGridCell = ({ props, type }) => {
    return (
      <ProcessGridCell
        type={type}
        props={props}
        navigate={navigate}
        searchParams={searchParams}
      />
    );
  };

  const renderRunIdCell = (props) => {
    return (
      <RunIdCell
        {...props}
        searchParams={searchParams}
      />
    );
  };



  return (
    <>
          <Form>
        <Row className="bg-light-primary px-4 py-2">
          <Col>
            <Row className="align-items-end g-2">
              <Col xs="12" sm="auto" className="flex-grow-0">
                <Label for="intervallo" className="mb-1 small">Interval</Label>
                <FilterIntervallo />
              </Col>
              
              {filterIntervallo < 0 && filterIntervallo != -3 && (
                <Col xs="12" sm="auto" className="flex-grow-0">
                  <Label for="dataDal" className="mb-1 small">From</Label>
                  <FilterDataDa name="DataDal" />
                </Col>
              )}
              
              {filterIntervallo < 0 &&
                filterIntervallo != -3 &&
                filterIntervallo != -2 && (
                  <Col xs="12" sm="auto" className="flex-grow-0">
                    <Label for="dataAl" className="mb-1 small">To</Label>
                    <FilterDataA name="DataAl" />
                  </Col>
                )}
              
              <Col xs="12" sm="auto" className="flex-grow-1">
                <Label for="processRunsDataProductTreeSelector" className="mb-1 small">Data Products</Label>
                <DataProductTreeSelector id="processRunsDataProductTreeSelector" />
              </Col>
              
              <Col xs="12" sm="auto" className="flex-grow-1">
                <Label for="processRunsProcessSelector" className="mb-1 small">Process Types</Label>
                <ProcessSelector id="processRunsProcessSelector" />
              </Col>
              
              <Col xs="12" sm="auto" className="flex-grow-1">
                <Label for="processRunsExtendedStatsSelector" className="mb-1 small">Version Statuses</Label>
                <ExtendedStatsSelector id="processRunsExtendedStatsSelector" />
              </Col>

              <Col xs="12" sm="auto" className="d-flex align-items-end ms-sm-auto">
                <FilterSubmit color="primary" text="Filter" />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <Container fluid className="py-2 px-4  position-relative ">
        <Row className="row g-3 mb-3">
          <Col>
            <Nav pills>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: currentActiveTab === "1",
                  })}
                  onClick={() => {
                    toggle("1");
                  }}
                >
                  Process Run Timeline
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: currentActiveTab === "2",
                  })}
                  onClick={() => toggle("2")}
                >
                  Process Run List
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={currentActiveTab}>
              <TabPane
                className="grid_task_container_tabs border border-top-0"
                tabId="1"
              >
                <div className="py-2 px-3">
                  <p className="my-2 lh-sm">
                    Generated on {moment().format("D/M/Y")} at{" "}
                    {moment().format("H:m:s")}. Total Processes:{" "}
                    {processRunsList ? processRunsList?.length : 0}
                  </p>
                  <Chart
                    style={{
                      height: 460,
                    }}
                    transitions={false}
                    onSeriesClick={onSeriesClick}
                  >
                    {/* <ChartTitle text="Process Runs" /> */}
                    <ChartTooltip render={renderTooltip} />
                    <ChartSeries
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      {transform(groupBy(processRunsList, "run_status"))?.map(
                        mapSeries
                      )}
                    </ChartSeries>
                    <ChartXAxis>
                      <ChartXAxisItem
                        title={{
                          text: "Start time",
                        }}
                        type="date"
                        baseUnit="days"
                        min={searchParams.get("fromDate")}
                        max={searchParams.get("toDate")}
                        majorUnit={diffDays() / 10}
                        minorUnit={diffDays()}
                        labels={{
                          format: "{0:dd/MM/yyyy}",
                        }}
                      />
                    </ChartXAxis>
                    <ChartYAxis>
                      <ChartYAxisItem
                        title={{
                          text: "Duration",
                        }}
                        labels={{
                          format: "{0}s",
                        }}
                      />
                    </ChartYAxis>
                    <ChartLegend
                      position="bottom"
                      visible={true}
                      offsetY={20}
                    />
                  </Chart>
                </div>
              </TabPane>

              <TabPane
                className="grid_task_container_tabs border border-top-0"
                tabId="2"
              >
                <Tooltip
                  openDelay={100}
                  position="right"
                  anchorElement="target"
                >
                  <div className="grid_task_container border">
                    <Grid
                      style={{ height: "100%" }}
                      pageable={true}
                      sortable={true}
                      total={dataResult?.length}
                      data={dataResult}
                      {...dataState}
                      groupable={true}
                      onDataStateChange={(e) => {
                        setDataState(e.dataState);
                        setDataResult(process(processRunsList, e.dataState));
                      }}
                      pager={(props) => GridPager(props, processRunsList)}
                    >
                      <Column
                        {...ColumnProps("run_id", dataState)}
                        title="Run"
                        
                        cell={renderRunIdCell}
                        columnMenu={ColumnMenu}
                      />
                      <Column
                        {...ColumnProps("process_name", dataState)}
                        title="Process"
                        cell={(props) => renderProcessGridCell({ props, type: "process_name" })}
                        columnMenu={ColumnMenu}
                      />
                      <Column
                        {...ColumnProps("run_status", dataState)}
                        title="Execution Status"
                        width="155"
                        columnMenu={ColumnMenu}
                      />
                      <Column
                        {...ColumnProps("run_start_ts", dataState)}
                        title="Start Time"
                        width={200}
                        cell={(props) => DateCell(props, true)}
                        columnMenu={ColumnMenu}
                      />
                      <Column
                        {...ColumnProps("duration", dataState)}
                        title="Duration"
                        width="120"
                        columnMenu={ColumnMenu}
                      />
                      <Column
                        {...ColumnProps("version_id", dataState)}
                        title="Version"
                        width="120"
                        cell={(props) => renderProcessGridCell({ props, type: "version_id" })}

                        columnMenu={ColumnMenu}
                      />
                      <Column
                        {...ColumnProps("product_name", dataState)}
                        title="Data Product"
                        cell={(props) => AtlanCell(props, "product_name", "product_type_id")}
                        columnMenu={ColumnMenu}
                      />
                      <Column
                        {...ColumnProps("ext_status_name", dataState)}
                        title="Extended Version Status"
                        columnMenu={ColumnMenu}
                      />
                    </Grid>
                  </div>
                </Tooltip>
              </TabPane>
            </TabContent>
          </Col>
        </Row>

        <LoaderSpin isVisible={isSpinnerVisible} />
        <FilterOverlay isVisible={dataCheck} />
      </Container>
    </>
  );
};

export default ProcessRuns;
